import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

/** Wait until nested img/video can paint (or give up after timeout). */
export const whenMediaReady = (mediaNode, callback) => {
  if (!mediaNode) {
    callback();
    return () => {};
  }

  const img =
    mediaNode.tagName === "IMG" ? mediaNode : mediaNode.querySelector("img");
  const video =
    mediaNode.tagName === "VIDEO" ? mediaNode : mediaNode.querySelector("video");

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    callback();
  };

  if (img) {
    if (img.complete && img.naturalWidth > 0) {
      finish();
      return () => {};
    }
    img.addEventListener("load", finish);
    img.addEventListener("error", finish);
    const t = setTimeout(finish, 2000);
    return () => {
      clearTimeout(t);
      img.removeEventListener("load", finish);
      img.removeEventListener("error", finish);
    };
  }

  if (video) {
    if (video.readyState >= 2) {
      finish();
      return () => {};
    }
    video.addEventListener("loadeddata", finish);
    video.addEventListener("error", finish);
    const t = setTimeout(finish, 2000);
    return () => {
      clearTimeout(t);
      video.removeEventListener("loadeddata", finish);
      video.removeEventListener("error", finish);
    };
  }

  finish();
  return () => {};
};

const createClipRevealTimeline = ({
  targetMedia,
  containerEl,
  initialScale = 1.08,
  targetScale = 1.0,
  duration = 0.8,
  ease = "power4.inOut",
  mediaEase = "power3.out",
  delay = 0,
}) => {
  gsap.set(containerEl, {
    clipPath: "inset(0% 100% 0% 0%)",
    willChange: "clip-path",
  });
  gsap.set(targetMedia, {
    scale: initialScale,
    transformOrigin: "center center",
    willChange: "transform",
  });

  return gsap.timeline({
    paused: true,
    delay,
    onComplete: () => {
      gsap.set(containerEl, { clearProps: "clipPath,willChange" });
      gsap.set(targetMedia, { clearProps: "willChange" });
      if (targetScale === 1.0) {
        gsap.set(targetMedia, { clearProps: "transform" });
      }
    },
  })
    .to(
      containerEl,
      {
        clipPath: "inset(0% 0% 0% 0%)",
        duration,
        ease,
      },
      0
    )
    .to(
      targetMedia,
      {
        scale: targetScale,
        duration: duration * 1.15,
        ease: mediaEase,
      },
      0
    );
};

/**
 * MediaReveal (React Component)
 * Left-to-right unmask + scale. Plays only when media is ready AND in view.
 */
const MediaReveal = ({
  children,
  className = "",
  innerClassName = "",
  duration = 0.8,
  ease = "power4.inOut",
  mediaEase = "power3.out",
  delay = 0,
  initialScale = 1.08,
  targetScale = 1.0,
  borderRadius = null,
  hoverZoom = false,
  triggerOffset = "top 92%",
  style = {},
  innerStyle = {},
  as: Component = "div",
}) => {
  const containerRef = useRef(null);
  const mediaRef = useRef(null);
  const playedRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    const media = mediaRef.current;
    if (!container || !media) return;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      gsap.set(container, { clearProps: "clipPath" });
      gsap.set(media, { clearProps: "transform" });
      return;
    }

    const roundClip = borderRadius ? ` round ${borderRadius}` : "";
    let inView = false;
    let mediaReady = false;
    let st = null;
    let observer = null;
    let resizeObserver = null;

    gsap.set(container, {
      clipPath: `inset(0% 100% 0% 0%${roundClip})`,
      willChange: "clip-path",
    });
    gsap.set(media, {
      scale: initialScale,
      transformOrigin: "center center",
      willChange: "transform",
    });

    const runAnimation = () => {
      if (playedRef.current || !inView || !mediaReady) return;
      playedRef.current = true;

      observer?.disconnect();
      st?.kill();
      resizeObserver?.disconnect();

      const tl = gsap.timeline({
        delay,
        onComplete: () => {
          gsap.set(container, { clearProps: "clipPath,willChange" });
          gsap.set(media, { clearProps: "willChange" });
          if (targetScale === 1.0) {
            gsap.set(media, { clearProps: "transform" });
          }
        },
      });

      tl.to(
        container,
        {
          clipPath: `inset(0% 0% 0% 0%${roundClip})`,
          duration,
          ease,
        },
        0
      );

      tl.to(
        media,
        {
          scale: targetScale,
          duration: duration * 1.15,
          ease: mediaEase,
        },
        0
      );
    };

    const markInView = () => {
      inView = true;
      runAnimation();
    };

    const cleanupMediaWait = whenMediaReady(media, () => {
      mediaReady = true;
      ScrollTrigger.refresh();
      const next = container.getBoundingClientRect();
      if (next.top < window.innerHeight * 0.95 && next.bottom > 0) {
        markInView();
      }
    });

    const rect = container.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.95 && rect.bottom > 0) {
      markInView();
    }

    st = ScrollTrigger.create({
      trigger: container,
      start: triggerOffset,
      once: true,
      // Play once on enter — never reverse / fade out on scroll up
      onEnter: markInView,
    });

    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) markInView();
        },
        { threshold: 0, rootMargin: "0px 0px -5% 0px" }
      );
      observer.observe(container);
    }

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        if (playedRef.current) return;
        ScrollTrigger.refresh();
        const next = container.getBoundingClientRect();
        if (next.top < window.innerHeight * 0.95 && next.bottom > 0) {
          markInView();
        }
      });
      resizeObserver.observe(container);
    }

    // Failsafe: never leave a blank clipped box if triggers miss
    const failsafe = setTimeout(() => {
      if (playedRef.current) return;
      const next = container.getBoundingClientRect();
      const near =
        next.top < window.innerHeight * 1.1 && next.bottom > -80;
      if (near) {
        inView = true;
        mediaReady = true;
        runAnimation();
      }
    }, 2500);

    return () => {
      clearTimeout(failsafe);
      cleanupMediaWait();
      st?.kill();
      observer?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [
    duration,
    ease,
    mediaEase,
    delay,
    initialScale,
    targetScale,
    borderRadius,
    triggerOffset,
  ]);

  return (
    <Component
      ref={containerRef}
      className={`riotters-reveal-mask tw:relative tw:overflow-hidden tw:bg-white ${className}`}
      style={{
        ...(borderRadius ? { borderRadius } : {}),
        ...style,
      }}
    >
      <div
        ref={mediaRef}
        className={`riotters-reveal-media ${hoverZoom ? "riotters-hover-zoom" : ""} ${innerClassName}`}
        style={{
          ...innerStyle,
        }}
      >
        {children}
      </div>
    </Component>
  );
};

// Portfolio listing + legacy portfolio detail pages only.
// Do NOT target case-study / service containers (e.g. logo banners).
const MEDIA_SELECTOR = [
  ".portfolio_section img",
  ".page_bg img",
  ".page_bg video",
  ".page_bg .section_detail img",
  ".page_bg .section_detail video",
  ".page_bg .header_padding img",
  ".page_bg .header_padding video",
  ".page_bg .side-inline-space img",
  ".page_bg .gaps img",
  ".page_bg .strenth_bg img",
  ".page_bg .image_section img",
].join(", ");

const shouldSkipMedia = (mediaEl) => {
  if (!mediaEl || mediaEl.getAttribute("data-riotters-reveal") === "true") {
    return true;
  }
  if (
    mediaEl.closest(".riotters-reveal-mask") ||
    mediaEl.closest(".riotters-auto-reveal-wrapper") ||
    mediaEl.closest("header") ||
    mediaEl.closest("nav") ||
    mediaEl.closest("footer") ||
    mediaEl.closest(".whatsapp-button") ||
    mediaEl.closest(".official_web") ||
    mediaEl.closest(".banner_logo") ||
    mediaEl.classList.contains("banner_logo") ||
    mediaEl.classList.contains("seo_marrow") ||
    mediaEl.classList.contains("arrow_img")
  ) {
    return true;
  }

  const src = (mediaEl.getAttribute("src") || "").toLowerCase();
  // Logos / wordmarks must never get the project-image reveal treatment
  if (
    src.endsWith(".svg") ||
    src.includes("logo") ||
    src.includes("wordmark")
  ) {
    return true;
  }

  return false;
};

const wrapMediaForReveal = (mediaEl) => {
  let wrapper = mediaEl.parentElement;
  if (!wrapper) return null;

  const parentStyle = window.getComputedStyle(wrapper);
  const parentHasRelative =
    parentStyle.position === "relative" || parentStyle.position === "absolute";

  // Keep existing single-child relative parents; otherwise wrap only the media node.
  if (!parentHasRelative || wrapper.children.length > 1) {
    const newWrapper = document.createElement("div");
    newWrapper.className = "riotters-auto-reveal-wrapper tw:relative tw:overflow-hidden";
    // Match the media box — never force full-width (breaks centered logos / max-w images)
    newWrapper.style.display = "block";
    newWrapper.style.width = "100%";
    newWrapper.style.maxWidth = "100%";
    newWrapper.style.lineHeight = "0";

    const mediaRadius = window.getComputedStyle(mediaEl).borderRadius;
    if (mediaRadius && mediaRadius !== "0px") {
      newWrapper.style.borderRadius = mediaRadius;
    } else if (parentStyle.borderRadius && parentStyle.borderRadius !== "0px") {
      newWrapper.style.borderRadius = parentStyle.borderRadius;
    }

    mediaEl.parentNode.insertBefore(newWrapper, mediaEl);
    newWrapper.appendChild(mediaEl);
    wrapper = newWrapper;
  } else {
    wrapper.classList.add("tw:relative", "tw:overflow-hidden");
  }

  return wrapper;
};

const attachRevealToMedia = (mediaEl, cleanups) => {
  if (shouldSkipMedia(mediaEl)) return;

  mediaEl.setAttribute("data-riotters-reveal", "true");
  const wrapper = wrapMediaForReveal(mediaEl);
  if (!wrapper) return;

  const tl = createClipRevealTimeline({
    targetMedia: mediaEl,
    containerEl: wrapper,
  });

  let inView = false;
  let mediaLoaded = false;
  let played = false;
  let st = null;
  let observer = null;

  const tryPlay = () => {
    if (inView && mediaLoaded && !played) {
      played = true;
      observer?.disconnect();
      st?.kill();
      tl.play();
    }
  };

  const cleanupMedia = whenMediaReady(mediaEl, () => {
    mediaLoaded = true;
    ScrollTrigger.refresh();
    const rect = wrapper.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.95 && rect.bottom > 0) {
      inView = true;
    }
    tryPlay();
  });

  const wrapRect = wrapper.getBoundingClientRect();
  if (wrapRect.top < window.innerHeight * 0.95 && wrapRect.bottom > 0) {
    inView = true;
    tryPlay();
  }

  st = ScrollTrigger.create({
    trigger: wrapper,
    start: "top 90%",
    once: true,
    onEnter: () => {
      inView = true;
      tryPlay();
    },
  });

  if (typeof IntersectionObserver !== "undefined") {
    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          inView = true;
          tryPlay();
        }
      },
      { threshold: 0, rootMargin: "0px 0px -5% 0px" }
    );
    observer.observe(wrapper);
  }

  cleanups.push(() => {
    cleanupMedia();
    st?.kill();
    observer?.disconnect();
    tl.kill();
  });
};

/**
 * GlobalMediaRevealManager
 * Auto-applies the same reveal to portfolio listing + legacy portfolio detail
 * images/videos without changing page markup or layout.
 */
export const GlobalMediaRevealManager = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const cleanups = [];
    let scanTimer = null;

    const scan = () => {
      document.querySelectorAll(MEDIA_SELECTOR).forEach((el) => {
        attachRevealToMedia(el, cleanups);
      });
      ScrollTrigger.refresh();
    };

    // Initial + short delayed pass (lazy images / filtered portfolio tabs)
    scanTimer = setTimeout(scan, 60);
    const secondPass = setTimeout(scan, 600);

    const mo =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver((mutations) => {
            let needsScan = false;
            for (const mutation of mutations) {
              if (mutation.addedNodes?.length) {
                needsScan = true;
                break;
              }
            }
            if (!needsScan) return;
            clearTimeout(scanTimer);
            scanTimer = setTimeout(scan, 80);
          })
        : null;

    mo?.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(scanTimer);
      clearTimeout(secondPass);
      mo?.disconnect();
      cleanups.forEach((fn) => fn());
    };
  }, [location.pathname, location.search]);

  return null;
};

export default MediaReveal;
